import {
    CompoundEntityRef,
    DEFAULT_NAMESPACE,
    parseEntityRef,
    stringifyEntityRef,
  } from '@backstage/catalog-model';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import {
  DocsIcon,
  HeaderIconLinkRow,
  IconLinkVerticalProps,
  InfoCardVariants,
} from '@backstage/core-components';
import {
  ScmIntegrationIcon,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import {
  alertApiRef,
  ErrorApiError,
  errorApiRef,
  useApi,
  useApp,
  useRouteRef,
} from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  getEntitySourceLocation,
  useEntity,
} from '@backstage/plugin-catalog-react';  
import { isTemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { AboutContent } from '@backstage/plugin-catalog';
import React, { useCallback } from 'react';
import { Tooltip } from '@material-ui/core';
import { ArrowUpward, DeleteForever, Edit, FileCopy } from '@material-ui/icons';
import CachedIcon from '@material-ui/icons/Cached';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import { viewTechDocRouteRef, createFromTemplateRouteRef } from './routes';

const ANNOTATION_UPGRADE_URL = 'backstage.io/upgrade-entity';
const ANNOTATION_EDIT_URL = 'backstage.io/edit-entity';
const ANNOTATION_CLONE_URL = 'backstage.io/clone-entity';
const ANNOTATION_DELETE_URL = 'backstage.io/delete-entity';
const TECHDOCS_ANNOTATION = 'backstage.io/techdocs-ref';
const TECHDOCS_EXTERNAL_ANNOTATION = 'backstage.io/techdocs-entity';

const useStyles = makeStyles({
  gridItemCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 10px)',
    marginBottom: '10px',
  },
  fullHeightCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  gridItemCardContent: {
    flex: 1,
  },
  fullHeightCardContent: {
    flex: 1,
  },
});

export interface AboutCardProps {
  variant?: InfoCardVariants;
  links?: Array<{ icon: React.ReactNode, url: string, tooltip?: string }>;
}

export function EntityAboutCardExtension(props: AboutCardProps) {
  const { variant } = props;
  const viewTechdocLink = useRouteRef(viewTechDocRouteRef);
  const classes = useStyles();
  const { entity } = useEntity();
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const app = useApp();
  const templateRoute = useRouteRef(createFromTemplateRouteRef);


  const entitySourceLocation = getEntitySourceLocation(
    entity,
    scmIntegrationsApi,
  );

  let techdocsRef: CompoundEntityRef | undefined;

  if (entity.metadata.annotations?.[TECHDOCS_EXTERNAL_ANNOTATION]) {
    try {
      techdocsRef = parseEntityRef(
        entity.metadata.annotations?.[TECHDOCS_EXTERNAL_ANNOTATION],
      );
    } catch {
      techdocsRef = undefined;
    }
  }
  
  const viewInSource: IconLinkVerticalProps = {
    label: 'View Source',
    disabled: !entitySourceLocation,
    icon: <ScmIntegrationIcon type={entitySourceLocation?.integrationType} />,
    href: entitySourceLocation?.locationTargetUrl,
  };
  const viewInTechDocs: IconLinkVerticalProps = {
    label: 'View Techdocs',
    disabled:
      !(
        entity.metadata.annotations?.[TECHDOCS_ANNOTATION] ||
        entity.metadata.annotations?.[TECHDOCS_EXTERNAL_ANNOTATION]
      ) || !viewTechdocLink,
    icon: <DocsIcon />,
    href:
      viewTechdocLink &&
      (techdocsRef
        ? viewTechdocLink({
            namespace: techdocsRef.namespace || DEFAULT_NAMESPACE,
            kind: techdocsRef.kind,
            name: techdocsRef.name,
          })
        : viewTechdocLink({
            namespace: entity.metadata.namespace || DEFAULT_NAMESPACE,
            kind: entity.kind,
            name: entity.metadata.name,
          })),
  };

  const subHeaderLinks = [viewInSource, viewInTechDocs];

  if (isTemplateEntityV1beta3(entity)) {
    const Icon = app.getSystemIcon('scaffolder') ?? CreateComponentIcon;

    const launchTemplate: IconLinkVerticalProps = {
      label: 'Launch Template',
      icon: <Icon />,
      disabled: !templateRoute,
      href:
        templateRoute &&
        templateRoute({
          templateName: entity.metadata.name,
          namespace: entity.metadata.namespace || DEFAULT_NAMESPACE,
        }),
    };

    subHeaderLinks.push(launchTemplate);
  }
  
  let cardClass = '';
  if (variant === 'gridItem') {
    cardClass = classes.gridItemCard;
  } else if (variant === 'fullHeight') {
    cardClass = classes.fullHeightCard;
  }

  let cardContentClass = '';
  if (variant === 'gridItem') {
    cardContentClass = classes.gridItemCardContent;
  } else if (variant === 'fullHeight') {
    cardContentClass = classes.fullHeightCardContent;
  }

  // Get metadata URL
  const entityMetadataUpgradeUrl = entity.metadata.annotations?.[ANNOTATION_UPGRADE_URL];
  const entityMetadataEditUrl = entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
  const entityMetadataCloneUrl = entity.metadata.annotations?.[ANNOTATION_CLONE_URL];
  const entityMetadataDeleteUrl = entity.metadata.annotations?.[ANNOTATION_DELETE_URL];

  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: 'Refresh scheduled',
        severity: 'info',
        display: 'transient',
      });
    } catch (e) {
      errorApi.post(e as ErrorApiError);
    }
  }, [catalogApi, entity, alertApi, errorApi]);
  
  return (
    <Card className={cardClass}>
      <CardHeader
          title={'About'}
          action={
          <div>
              <Tooltip title={'Upgrade'}>
                  <IconButton
                      disabled={!entityMetadataUpgradeUrl}
                      href={entityMetadataUpgradeUrl ?? ""}
                  >
                      <ArrowUpward/>
                  </IconButton>
              </Tooltip>
              <Tooltip title={'Refresh'}>
                <IconButton
                  aria-label="Refresh"
                  title={'aboutCard.refreshButtonTitle'}
                  onClick={refreshEntity}
                >
                <CachedIcon />
            </IconButton>

              </Tooltip>
              <Tooltip title={'Edit'}>
                  <IconButton
                      disabled={!entityMetadataEditUrl}
                      href={entityMetadataEditUrl ?? ""}
                  >
                      <Edit/>
                  </IconButton>
              </Tooltip>
              <Tooltip title={'Clone'}>
                  <IconButton
                      disabled={!entityMetadataCloneUrl}
                      href={entityMetadataCloneUrl ?? ""}
                  >
                      <FileCopy/>
                  </IconButton>
              </Tooltip>
              <Tooltip title={'Delete'}>
                  <IconButton
                      disabled={!entityMetadataDeleteUrl}
                      href={entityMetadataDeleteUrl ?? ""}
                  >
                      <DeleteForever/>
                  </IconButton>
              </Tooltip>
          </div>
          }
          subheader={<HeaderIconLinkRow links={subHeaderLinks} />}
      />
      <Divider />
      <CardContent className={cardContentClass}>
          <AboutContent entity={entity} />
      </CardContent>
    </Card>
  );
}